// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../connections/AuthContext';
import routes_web from '../../connections/routes_web';
import { fetchCoreFrameworks, fetchCoreCycles } from './core_services';

// imported components
import TabCoreFrameworks from './tab_frameworks/main_frameworks';
import TabCorePurpose from './tab_purpose/main_purpose';
import TabCoreStrategy from './tab_strategy/main_strategy';
import TabCoreTactical from './tab_tactical/main_tactical';
import TabCoreSettings from './tab_settings/main_settings';
import NavbarTop from './moduleComponents/NavbarTop';
import NavbarBottom from './moduleComponents/NavbarBottom';


// ###### MAIN COMPONENT
// Rebuilt 2026-08-19 — Core is now a personal-goals hub organized around 3
// time horizons (purpose/strategy/tactical, each a CoreCycle) plus a
// reusable framework repository (CoreFramework), instead of the old
// structure/definitions/goals/controls placeholder tabs. See design notes,
// 2026-08-19, for the full rationale (no FK between horizons by design,
// etc).
const CoreMain = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('frameworks');
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(true);
  const [purposeCycles, setPurposeCycles] = useState([]);
  const [strategyCycles, setStrategyCycles] = useState([]);
  const [tacticalCycles, setTacticalCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(true);

  useEffect(() => {
    loadFrameworks();
    loadCycles();
  }, []);

  // ### API REQUESTS
  const loadFrameworks = async () => {
    try {
      const response = await fetchCoreFrameworks();
      setFrameworks(response.data);
    } catch (err) {
      console.error('Error fetching core frameworks:', err);
    } finally {
      setFrameworksLoading(false);
    }
  };

  // Cycles are fetched once per horizon (three calls) rather than one
  // unfiltered call split client-side — mirrors how filtering already works
  // server-side via ?horizon= on CoreCycleViewSet.get_queryset.
  const loadCycles = async () => {
    try {
      const [purposeRes, strategyRes, tacticalRes] = await Promise.all([
        fetchCoreCycles('purpose'),
        fetchCoreCycles('strategy'),
        fetchCoreCycles('tactical'),
      ]);
      setPurposeCycles(purposeRes.data);
      setStrategyCycles(strategyRes.data);
      setTacticalCycles(tacticalRes.data);
    } catch (err) {
      console.error('Error fetching core cycles:', err);
    } finally {
      setCyclesLoading(false);
    }
  };

  // ### HANDLERS
  const handleTabChange = (tab) => setActiveTab(tab);

  // ### RETURN VIEW
  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-4 pb-24">
      <div className="max-w-5xl w-full mx-auto pt-14">

        {/* Tabs Content */}
        {activeTab === 'frameworks' && (
          <TabCoreFrameworks frameworks={frameworks} loadFrameworks={loadFrameworks} isLoading={frameworksLoading} />
        )}
        {activeTab === 'purpose' && (
          <TabCorePurpose cycles={purposeCycles} loadCycles={loadCycles} frameworks={frameworks} isLoading={cyclesLoading} />
        )}
        {activeTab === 'strategy' && (
          <TabCoreStrategy cycles={strategyCycles} loadCycles={loadCycles} frameworks={frameworks} isLoading={cyclesLoading} />
        )}
        {activeTab === 'tactical' && (
          <TabCoreTactical cycles={tacticalCycles} loadCycles={loadCycles} frameworks={frameworks} isLoading={cyclesLoading} />
        )}
        {activeTab === 'settings' && <TabCoreSettings />}

      </div>

      {/* Navbar Top */}
      <NavbarTop
        title="Core"
        onHomeClick={() => navigate(routes_web.accounts_home)}
        onLogoutClick={logout}
        handleTabChange={handleTabChange}
        activeTab={activeTab}
      />

      {/* Navbar Bottom */}
      <NavbarBottom activeTab={activeTab} handleTabChange={handleTabChange} />
    </div>
  );
};

export default CoreMain;
